import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { DollarSign, TrendingUp, Clock } from 'lucide-react';
import _ from 'lodash';
import { collection, onSnapshot } from 'firebase/firestore'; // Replace getDocs with onSnapshot
import { db, auth } from '../firebaseConfig'; // Import your Firestore instance and auth
import { Link, useLocation } from 'react-router-dom'; // Ensure Link and useLocation are imported
import { onAuthStateChanged } from 'firebase/auth'; // Import authentication listener
import AdminInvoices from './AdminInvoices'; // Add this import

const InvoiceDashboard = ({ lang }) => {
  const location = useLocation();
  const invoiceData = location.state?.invoiceData || null;
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Subscribe to both quotes and invoices collections
        const unsubscribeInvoices = onSnapshot(collection(db, "invoices"), (snapshot) => {
          const invoicesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            source: 'invoice'
          }));
          setInvoices(_.orderBy(invoicesData, ['date'], ['desc']));
        });

        return () => {
          unsubscribeInvoices();
        };
      } else {
        setInvoices([]);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (invoiceData) {
      setInvoices(prev => [...prev, invoiceData]);
    }
  }, [invoiceData]);

  // Calculate summary metrics with null checks
  const totalAmount = invoices.reduce((sum, invoice) => sum + (Number(invoice?.amount) || 0), 0);
  const paidInvoices = invoices.filter(invoice => invoice?.status === 'Paid').length;
  const pendingAmount = invoices
    .filter(inv => inv?.status === 'Pending' || inv?.status === 'Overdue')
    .reduce((sum, inv) => sum + (Number(inv?.amount) || 0), 0);

  // Prepare monthly data for the graph with null checks
  const graphData = _(invoices)
    .filter(inv => inv?.date) // Filter out entries without dates
    .groupBy(inv => inv.date.substring(0, 7))
    .map((group, month) => ({
      month: new Date(month).toLocaleString('default', { month: 'short' }),
      total: group.reduce((sum, inv) => sum + (Number(inv?.amount) || 0), 0),
      paid: group
        .filter(inv => inv?.status === 'Paid')
        .reduce((sum, inv) => sum + (Number(inv?.amount) || 0), 0),
      pending: group
        .filter(inv => inv?.status === 'Pending')
        .reduce((sum, inv) => sum + (Number(inv?.amount) || 0), 0),
      overdue: group
        .filter(inv => inv?.status === 'Overdue')
        .reduce((sum, inv) => sum + (Number(inv?.amount) || 0), 0)
    }))
    .value();

  const statusColorMap = {
    'Paid': 'bg-green-100 text-green-800',
    'Pending': 'bg-yellow-100 text-yellow-800',
    'Overdue': 'bg-red-100 text-red-800'
  };

  const handleViewMore = (invoice, event) => {
    const rect = event.target.getBoundingClientRect();
    setSelectedInvoice(invoice);
  };

  return (
    <div className="space-y-6 p-6 bg-gray-50">
      <AdminInvoices language={lang} /> {/* Make sure to pass the language prop */}
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">{lang === 'th' ? 'รายได้รวม' : 'Total Revenue'}</p>
              <h3 className="text-2xl font-bold">${totalAmount.toLocaleString()}</h3>
            </div>
            <DollarSign className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">{lang === 'th' ? 'จำนวนที่ค้างชำระ' : 'Pending Amount'}</p>
              <h3 className="text-2xl font-bold">${pendingAmount.toLocaleString()}</h3>
            </div>
            <Clock className="h-8 w-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">{lang === 'th' ? 'ใบแจ้งหนี้ที่ชำระแล้ว' : 'Paid Invoices'}</p>
              <h3 className="text-2xl font-bold">{paidInvoices} / {invoices.length || 0}</h3>
            </div>
            <TrendingUp className="h-8 w-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Revenue Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4">{lang === 'th' ? 'แนวโน้มรายได้รายเดือน' : 'Monthly Revenue Trend'}</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={graphData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke="#2563eb" 
                  strokeWidth={2} 
                  name={lang === 'th' ? 'รายได้รวม' : 'Total Revenue'}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4">{lang === 'th' ? 'รายได้ตามสถานะ' : 'Revenue by Status'}</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={graphData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="paid" stackId="a" fill="#16a34a" name={lang === 'th' ? 'ชำระแล้ว' : 'Paid'} />
                <Bar dataKey="pending" stackId="a" fill="#eab308" name={lang === 'th' ? 'ค้างชำระ' : 'Pending'} />
                <Bar dataKey="overdue" stackId="a" fill="#dc2626" name={lang === 'th' ? 'เกินกำหนด' : 'Overdue'} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <h2 className="text-2xl font-bold mb-6">{lang === 'th' ? 'ใบแจ้งหนี้ล่าสุด' : 'Recent Invoices'}</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{lang === 'th' ? 'รหัสใบแจ้งหนี้' : 'Invoice ID'}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{lang === 'th' ? 'วันที่' : 'Date'}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{lang === 'th' ? 'ลูกค้า' : 'Client'}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{lang === 'th' ? 'จำนวนเงิน' : 'Amount'}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <tr 
                    key={invoice?.id || Math.random()}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{(invoice?.id || '').toString().padStart(5, '0')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice?.date ? new Date(invoice.date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invoice?.paymentDue ? new Date(invoice.paymentDue).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {invoice?.client || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${(Number(invoice?.amount) || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColorMap[invoice?.status || 'Pending']}`}>
                        {invoice?.status || 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {invoice.quoteId && (
                        <span className="text-sm text-gray-500">
                          Quote #{invoice.quoteId}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={(e) => handleViewMore(invoice, e)}
                        className="text-blue-500 hover:text-blue-700 transition-colors duration-150"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Enhanced Tooltip/Modal for Invoice Details */}
      {selectedInvoice && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setSelectedInvoice(null)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 transform transition-all"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Invoice Details</h3>
              <button 
                onClick={() => setSelectedInvoice(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="font-semibold">Invoice ID</p>
                <p className="text-gray-600">#{selectedInvoice.id.toString().padStart(5, '0')}</p>
              </div>
              <div>
                <p className="font-semibold">Client Name</p>
                <p className="text-gray-600">{selectedInvoice.client}</p>
              </div>
              <div>
                <p className="font-semibold">Date</p>
                <p className="text-gray-600">{selectedInvoice.date ? new Date(selectedInvoice.date).toLocaleDateString() : 'N/A'}</p>
              </div>
              <div>
                <p className="font-semibold">Amount</p>
                <p className="text-gray-600">${selectedInvoice.amount ? selectedInvoice.amount.toLocaleString() : 'N/A'}</p>
              </div>
              <div>
                <p className="font-semibold">Status</p>
                <p className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColorMap[selectedInvoice.status]}`}>
                  {selectedInvoice.status}
                </p>
              </div>
              {/* Add more details as needed */}
            </div>
          </div>
        </div>
      )}

      {/* Add navigation to Admin Invoices */}
      <div className="mt-6">
        <Link
          to="/admin/invoices"
          className="bg-blue-500 text-white px-4 py-2 rounded"
        >
          Manage Invoices
        </Link>
      </div>

      {invoiceData && (
        <div className="bg-white p-6 rounded-lg shadow mt-6">
          <h2 className="text-lg font-semibold mb-4">Invoice Details</h2>
          <p><strong>Client:</strong> {invoiceData.client}</p>
          <p><strong>Invoice ID:</strong> {invoiceData.id}</p>
          <p><strong>Date:</strong> {invoiceData.date ? new Date(invoiceData.date).toLocaleDateString() : 'N/A'}</p>
          <p><strong>Due Date:</strong> {invoiceData.paymentDue ? new Date(invoiceData.paymentDue).toLocaleDateString() : 'N/A'}</p>
          <p><strong>Amount:</strong> ${invoiceData.amount ? invoiceData.amount.toLocaleString() : 'N/A'}</p>
          <p><strong>Status:</strong> {invoiceData.status}</p>
          {/* Add more details as needed */}
        </div>
      )}
    </div>
  );
};

export default InvoiceDashboard;